import { useEffect, useState } from "react";

import { listBranches } from "./branches";
import { useActiveBranch } from "./use-active-branch";
import { useAuth } from "../auth/auth-context";

/**
 * Which country a phone number typed on this screen should be read against.
 *
 * ── Three answers, in order ─────────────────────────────────────────────────
 *
 *   1. **The location being worked in.** A practice with a branch either side
 *      of a border needs each one's numbers read against its own country, and
 *      until `branches.country` existed (decision D4) there was nothing to ask.
 *   2. **The clinic's own `defaultCountry`**, which is what every phone field
 *      used before and remains right for a single-country practice.
 *   3. **Nothing**, leaving the picker unselected. Never a guess: an
 *      unselected country asks a question, whereas a wrong one silently
 *      produces an unreachable number that looks correct.
 *
 * ── A prefill, never a lock ─────────────────────────────────────────────────
 *
 * Somebody hired at the Beirut branch may perfectly well carry a French
 * number, and a patient may be visiting. Every caller passes this to
 * `PhoneInput` as `defaultCountry`, which applies it only while the field is
 * empty and never overrides a number already recorded.
 */
export function usePhoneCountry(): string | null {
  const { user } = useAuth();
  const branchId = useActiveBranch();
  const [branchCountry, setBranchCountry] = useState<string | null>(null);

  useEffect(() => {
    if (branchId === null) {
      setBranchCountry(null);
      return;
    }

    const controller = new AbortController();

    listBranches(controller.signal)
      .then((branches) => {
        setBranchCountry(branches.find((branch) => branch.id === branchId)?.country ?? null);
      })
      .catch(() => {
        // Silent. This is a convenience, and the clinic default below is a
        // perfectly good answer — a failed read must not stop anybody typing a
        // number.
      });

    return () => controller.abort();
  }, [branchId]);

  return branchCountry ?? user?.clinicDefaultCountry ?? null;
}
